import {
  ResourceMetadataFilters,
  GetResourceMetadataRequest,
  CreateResourceMetadataClientData,
  CreateResourceMetadataRequest,
  UpdateResourceMetadataClientData,
  UpdateResourceMetadataRequest,
  BaseMutationOptions,
  ResolverMutationRequestMetadata,
  parseResolverResponse
} from '@esg/syntiro-api-core';
import { ResourceMetadata } from '@esg/esg-global-types';
import { FetchResult } from '@apollo/client';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../util/uuid';
import { FUNCTION_NO_DATA_FAILURE, FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { auth } from '../../lib/google/firebase';
import { validateParams, isValidFirestoreId } from '../validators/base.validator';
import {
  isValidResourceMetadataDownloadCount,
  isValidMasterListResourceReference
} from '../validators/resource_metadata.validator';
import { apollo_client_v2 } from '../../App';
import { GET_RESOURCE_METADATA } from '../queries/resource_metadata.queries';
import {
  CREATE_RESOURCE_METADATA,
  UPDATE_RESOURCE_METADATA
} from '../mutations/resource_metadata.mutations';

const SOURCE = 'resource_metadata.service.ts';

/**
 * Query all resource metadata for a company.
 * @param {ResolverMutationRequestMetadata} metadata Metadata containing group_id and company_id.
 * @param {ResourceMetadataFilters} filters Optional filter parameters.
 * @returns {Promise<Array<ResourceMetadata>>}
 */
export const getResourceMetadata = async (
  metadata: ResolverMutationRequestMetadata,
  filters: ResourceMetadataFilters = {}
): Promise<Array<ResourceMetadata>> => {
  const input: GetResourceMetadataRequest['input'] = {
    metadata,
    filter: filters
  };
  return apollo_client_v2
    .query({
      query: GET_RESOURCE_METADATA,
      variables: { input },
      fetchPolicy: 'no-cache'
    })
    .then((response: FetchResult) => {
      if (!response.data?.getResourceMetadata) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('getResourceMetadata'));
      }
      const result: Array<ResourceMetadata> | null | undefined = parseResolverResponse<
        Array<ResourceMetadata>
      >(response.data.getResourceMetadata);
      return result ?? [];
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'getResourceMetadata'),
        { metadata, filters },
        tracking_id
      );
    });
};

/**
 * Create a resource metadata entry for a company.
 * @param {ResolverMutationRequestMetadata} metadata Metadata containing group_id and company_id.
 * @param {CreateResourceMetadataClientData} data Required data to populate resource metadata fields.
 * @param {BaseMutationOptions} options Standard mutator options to pass to the API.
 * @returns {Promise<ResourceMetadata | null | undefined>}
 */
export const createResourceMetadata = async (
  metadata: ResolverMutationRequestMetadata,
  data: CreateResourceMetadataClientData,
  options: BaseMutationOptions = { return_data: true, create_audit_log: false }
): Promise<ResourceMetadata | null | undefined> => {
  validateParams(
    { data },
    {
      'data.download_count': {
        validate: isValidResourceMetadataDownloadCount,
        message: 'Invalid resource metadata download count specified.'
      },
      'data.master_list_resource': {
        validate: isValidMasterListResourceReference,
        message: 'Invalid master list resource reference specified.'
      }
    }
  );
  const input: CreateResourceMetadataRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      resource_metadata: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: CREATE_RESOURCE_METADATA,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.createResourceMetadata) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('createResourceMetadata'));
      }
      return parseResolverResponse<ResourceMetadata>(response.data.createResourceMetadata);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createResourceMetadata'),
        { metadata, data },
        tracking_id
      );
    });
};

/**
 * Update a resource metadata entry for a company.
 * @param {ResolverMutationRequestMetadata} metadata Metadata containing group_id and company_id.
 * @param {UpdateResourceMetadataClientData} data Resource metadata fields to update (must include id).
 * @param {BaseMutationOptions} options Standard mutator options to pass to the API.
 * @returns {Promise<ResourceMetadata | null | undefined>}
 */
export const updateResourceMetadata = async (
  metadata: ResolverMutationRequestMetadata,
  data: UpdateResourceMetadataClientData,
  options: BaseMutationOptions = { return_data: true, create_audit_log: false }
): Promise<ResourceMetadata | null | undefined> => {
  validateParams(
    { data },
    {
      'data.id': {
        validate: isValidFirestoreId,
        message: 'Invalid resource metadata id specified.'
      },
      'data.download_count': {
        validate: isValidResourceMetadataDownloadCount,
        message: 'Invalid resource metadata download count specified.',
        is_optional: true
      },
      'data.master_list_resource': {
        validate: isValidMasterListResourceReference,
        message: 'Invalid master list resource reference specified.',
        is_optional: true
      }
    }
  );
  const input: UpdateResourceMetadataRequest['input'] = {
    metadata: {
      ...metadata,
      audit_user_email: auth.currentUser?.email ?? ''
    },
    data: {
      resource_metadata: data
    },
    options
  };
  return apollo_client_v2
    .mutate({
      mutation: UPDATE_RESOURCE_METADATA,
      variables: { input }
    })
    .then((response: FetchResult) => {
      if (!response.data?.updateResourceMetadata) {
        throw new Error(FUNCTION_NO_DATA_FAILURE('updateResourceMetadata'));
      }
      return parseResolverResponse<ResourceMetadata>(response.data.updateResourceMetadata);
    })
    .catch((err: unknown) => {
      const tracking_id: string = generateUuidV4();
      throw new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateResourceMetadata'),
        { metadata, data },
        tracking_id
      );
    });
};
